/**
 * The face of the round language button: a "languages" glyph (文A, Lucide's
 * `languages` icon, ISC) and the target language's two-letter code, both in
 * the parent's text colour (`currentColor`) — no flags (owner, 2026-09-24): a
 * language is not a country, and the glyph says "language" on its own.
 *
 * Inline SVG, not emoji (Windows draws emoji inconsistently). No ids, so the
 * header and the open mobile menu can each mount one. SSR-safe (pure markup).
 *
 * Render it inside a `HEADER_CIRCLE` element (./sizes) with a background and
 * a text colour — one of the pairs langFace.contrast.test.ts checks (VV2:
 * `bg-vv2-paper text-vv2-ink`; Vercelli: `bg-brand-forest-dark
 * text-brand-cream`). The glyph and the code fit the 40 px circle side by
 * side, so the header budgets in ./sizes are unchanged. The whole face is
 * aria-hidden: the interactive parent carries the accessible name — the
 * language's own name ("English", "Italiano") as sr-only text, in that
 * language's `lang`.
 */

export type FaceLang = 'it' | 'en'

const CODE: Record<FaceLang, string> = { it: 'IT', en: 'EN' }

/**
 * STRUCTURE CONTRACT: the static legal pages (landings/src/legal/legal.css,
 * inlined — no Tailwind there) style this face by position — `.legal-circle > svg` = glyph,
 * `.legal-circle > span` = code. Keep the order svg → code span and the
 * element types, or update legal.css in the same change.
 *
 * A fragment, so the pieces sit directly in the circle; each one is
 * aria-hidden itself (a `display: contents` wrapper can lose its aria-hidden
 * in some browsers and would leak "EN" into the link's name).
 */
export function LangFace({ lang }: { lang: FaceLang }) {
  return (
    <>
      <svg
        aria-hidden="true"
        viewBox="0 0 24 24"
        className="h-3.5 w-3.5 shrink-0"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path d="m5 8 6 6" />
        <path d="m4 14 6-6 2-3" />
        <path d="M2 5h12" />
        <path d="M7 2h1" />
        <path d="m22 22-5-10-5 10" />
        <path d="M14 18h6" />
      </svg>
      <span aria-hidden="true" className="ml-0.5 text-[11px] font-bold leading-none tracking-[0.04em]">
        {CODE[lang]}
      </span>
    </>
  )
}
